/**
 * The "In brief" line, shared by every module.
 *
 * WHY IT IS SHARED (owner, 2026-08-31, on discovering the In Brief improvements had landed in Aged
 * Care only). Five modules each hand-rolled `clauses: string[]` and joined them into one grey
 * sentence. The wording had already drifted between them, and any improvement had to be made five
 * times — so it wasn't. Aged Care is the benchmark for UI across this app; this is that benchmark
 * expressed once.
 *
 * WHAT IT DOES NOT DO: flatten the modules. Each has its own story to tell first — a downsize frees
 * capital, a village entry pays a contribution, an aged care plan leaves part of a room unpaid — and
 * those clauses stay local. What is shared is the THREE CLAUSES EVERY MODULE ENDS WITH (the Age
 * Pension, cashflow, and the estate) plus the rendering, because those are the figures a client
 * scans for and they should look and read the same wherever they appear.
 */
import React from 'react';
import { formatCurrency } from '../../utils/index.ts';

/**
 * One clause of the In Brief.
 *
 * `term` is the phrase to bold — always the start of `text`. `tone` says whether the movement is
 * good or bad FOR THE CLIENT, which is not the same as up or down: a falling Age Pension is bad, a
 * falling daily accommodation payment is good.
 */
export interface RecapClause {
    text: string;
    term?: string;
    tone: 'good' | 'bad' | 'neutral';
    /**
     * A qualification that must NOT carry the clause's tone.
     *
     * "Cashflow is better by $6,600 a year, though it is still short by $8,400" — the improvement is
     * good news and the shortfall is not, so colouring the whole sentence green would use the tone
     * to contradict the words. Rendered in plain slate after the toned part.
     */
    caveat?: string;
    /**
     * Substrings to set in bold — the dollar figures a reader scans for (owner, 22 Sep 2026: "bold
     * the aged care cost … bold the $27,031 deficit in the explainer as well"). Applied to the text
     * and the caveat alike; the tone colour is unchanged.
     */
    emphasis?: string[];
}

/** The text with each `emphasis` substring set in bold; plain text when there is none. */
function withEmphasis(text: string, emphasis: string[] | undefined): React.ReactNode {
    const parts = (emphasis || []).filter((e) => e && text.includes(e));
    if (parts.length === 0) return text;
    const re = new RegExp(`(${parts.map((e) => e.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g');
    return text.split(re).map((piece, i) => (parts.includes(piece) ? <strong key={i} className="text-slate-800">{piece}</strong> : piece));
}

/**
 * The Age Pension clause.
 *
 * `reason` lets a module add its own "because…" — the Downsizer explains that the home is exempt and
 * what it turns into is not, which is the whole point of saying it there.
 */
export function pensionClause(delta: number, reason = ''): RecapClause {
    const flat = Math.round(delta) === 0;
    return {
        term: 'the Age Pension',
        tone: flat ? 'neutral' : delta > 0 ? 'good' : 'bad',
        text: flat
            ? 'the Age Pension does not move'
            : `the Age Pension ${delta > 0 ? 'rises' : 'falls'} by ${formatCurrency(Math.abs(delta))} a year${reason}`,
    };
}

/**
 * The cashflow clause, which states the LEVEL as well as the movement.
 *
 * OWNER-CAUGHT, 2026-08-31: "cashflow is better by $20,517" reads as a surplus when the plan is
 * still $51,229 in deficit every year. An improvement quoted without its level is the most
 * flattering true sentence available, and a client acting on it would badly misjudge what they can
 * afford. Pass `after` wherever the absolute figure is known.
 */
export function cashflowClause(delta: number, after?: number | null): RecapClause {
    const caveat = after != null && after < 0
        ? `, though it is still short by ${formatCurrency(Math.abs(after))} a year`
        : undefined;
    return {
        term: 'cashflow',
        tone: delta > 0 ? 'good' : 'bad',
        text: `cashflow is ${delta > 0 ? 'better' : 'worse'} by ${formatCurrency(Math.abs(delta))} a year`,
        caveat,
        // The shortfall is the figure the client must not miss, so it is bold as well as plain slate.
        emphasis: after != null && after < 0 ? [formatCurrency(Math.abs(after))] : undefined,
    };
}

/** The estate clause. `years` is the projection horizon the figure was measured over. */
export function estateClause(delta: number, years: number): RecapClause {
    const flat = Math.round(delta) === 0;
    const period = `${years} ${years === 1 ? 'year' : 'years'}`;
    return {
        term: 'the estate',
        tone: flat ? 'neutral' : delta > 0 ? 'good' : 'bad',
        text: flat
            ? `the estate is unchanged after ${period}`
            : `the estate ends ${formatCurrency(Math.abs(delta))} ${delta > 0 ? 'ahead' : 'behind'} after ${period}`,
    };
}

/** A module-specific clause that carries no direction — context, not a movement. */
export function plainClause(text: string, emphasis?: string[]): RecapClause {
    return { text, tone: 'neutral', emphasis };
}

/**
 * The In Brief, with the figures a reader is scanning for made findable.
 *
 * The term is bolded and the movement coloured by what it means for the client. "No change" stays
 * plain slate rather than amber: amber means something needs attention, and nothing moving needs
 * none — colouring it amber would teach a reader to discount amber where it does matter.
 *
 * Colour is never the only signal. The words say the same thing, so this reads identically to
 * someone who cannot distinguish the colours.
 */
export function RecapClauses({ clauses }: { clauses: RecapClause[] }) {
    const toneClass: Record<RecapClause['tone'], string> = {
        good: 'text-emerald-700 font-semibold',
        bad: 'text-amber-700 font-semibold',
        neutral: 'text-slate-600',
    };
    const usable = clauses.filter(Boolean);
    if (usable.length === 0) return null;
    return (
        <>
            {usable.map((clause, i) => {
                const hasTerm = !!clause.term && clause.text.startsWith(clause.term);
                const body = hasTerm ? clause.text.slice(clause.term!.length) : clause.text;
                const lead = i === 0 ? '' : i === usable.length - 1 ? ' and ' : ', ';
                const cap = (t: string) => (i === 0 ? `${t.charAt(0).toUpperCase()}${t.slice(1)}` : t);
                return (
                    <React.Fragment key={i}>
                        {lead}
                        {hasTerm ? (
                            <>
                                <strong className="text-slate-800">{cap(clause.term!)}</strong>
                                <span className={toneClass[clause.tone]}>{withEmphasis(body, clause.emphasis)}</span>
                            </>
                        ) : (
                            <span className={toneClass[clause.tone]}>{withEmphasis(cap(body), clause.emphasis)}</span>
                        )}
                        {clause.caveat && <span className="text-slate-600">{withEmphasis(clause.caveat, clause.emphasis)}</span>}
                        {i === usable.length - 1 ? '.' : ''}
                    </React.Fragment>
                );
            })}
        </>
    );
}

/**
 * The same clauses as one plain sentence.
 *
 * Kept for anything that cannot render markup — the SOA, a saved record, a test. DERIVED from the
 * clauses rather than built separately, so the sentence and the panel can never say different things.
 */
export function joinClauses(clauses: RecapClause[]): string | null {
    const texts = clauses.filter(Boolean).map((c) => `${c.text}${c.caveat || ''}`);
    if (texts.length === 0) return null;
    const joined = texts.length > 1 ? `${texts.slice(0, -1).join(', ')} and ${texts[texts.length - 1]}` : texts[0];
    return `${joined.charAt(0).toUpperCase()}${joined.slice(1)}.`;
}
